import { useId, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type Ref } from 'react';

// Eingabefeld mit Vorschlägen beim Tippen (Kunde aus der Liste, Leistung): eine Liste unter dem Feld, mit Maus,
// Finger oder Pfeiltasten und Enter wählbar, Esc schließt. Wird nichts gewählt, bleibt der getippte Text.
export interface Vorschlag {
  key: string;
  // Hauptzeile und kleine zweite Zeile (z. B. Anschrift oder Preis)
  label: ReactNode;
  sub?: ReactNode;
}

export function VorschlagInput({
  value,
  onChange,
  vorschlaege,
  onPick,
  inputProps,
  inputRef,
}: {
  value: string;
  onChange(v: string): void;
  // passende Vorschläge zum getippten Text (leer: keine Liste)
  vorschlaege: Vorschlag[];
  onPick(key: string): void;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const shown = open && vorschlaege.length > 0;

  function pick(key: string) {
    setOpen(false);
    setActive(-1);
    onPick(key);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape' && shown) {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!vorschlaege.length) return;
      e.preventDefault();
      setOpen(true);
      const n = vorschlaege.length;
      setActive((a) => (e.key === 'ArrowDown' ? (a + 1) % n : a <= 0 ? n - 1 : a - 1));
      return;
    }
    if (e.key === 'Enter' && shown && active >= 0 && active < vorschlaege.length) {
      e.preventDefault();
      pick(vorschlaege[active]!.key);
    }
  }

  return (
    <span className="vw">
      <input
        {...inputProps}
        ref={inputRef}
        value={value}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={`${id}-l`}
        aria-activedescendant={shown && active >= 0 && active < vorschlaege.length ? `${id}-${active}` : undefined}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={(e) => {
          setOpen(true);
          inputProps?.onFocus?.(e);
        }}
        onBlur={(e) => {
          setOpen(false);
          inputProps?.onBlur?.(e);
        }}
        onKeyDown={onKey}
      />
      {shown && (
        <ul className="vw-list" role="listbox" id={`${id}-l`}>
          {vorschlaege.map((v, i) => (
            <li
              key={v.key}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'on' : undefined}
              // vor dem Verlassen des Feldes wählen (sonst schließt die Liste zuerst)
              onMouseDown={(e) => {
                e.preventDefault();
                pick(v.key);
              }}
            >
              <span className="vw-l">{v.label}</span>
              {v.sub && <span className="vw-s">{v.sub}</span>}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
